import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'
import Promise from 'bluebird'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { Formatter } from 'shared-libs/helpers/formatter'
import { GeolocationError, getCurrentGeoPosition } from 'browser/app/utils/utils'
import type { UI2GeolocationFieldProps } from 'shared-libs/components/ui2/fields/ui2-geolocation-field/ui2-geolocation-field-props'
import { UI2_GEOLOCATION_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-geolocation-field/ui2-geolocation-field-props'

import { UI2FieldShell, type IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'

import './_ui2-geolocation-field.scss'

interface IUI2GeolocationFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  frames?: FramesManager
  translationTable?: any
  errors?: any[]
}

/**
 * @uiComponent
 */
export interface IUI2GeolocationFieldProps
  extends UI2GeolocationFieldProps,
    IUI2GeolocationFieldWebExtras {}

interface IUI2GeolocationFieldState {
  gpsError?: GeolocationError
  isLocating: boolean
  internalValue: any
}

let CACHED_LOCATION: { latitude: number; longitude: number } | undefined

export class UI2GeolocationField extends React.Component<
  IUI2GeolocationFieldProps,
  IUI2GeolocationFieldState
> {
  public static defaultProps: Partial<IUI2GeolocationFieldProps> = {
    ...UI2_GEOLOCATION_FIELD_DEFAULT_PROPS,
    minSecondsBetweenUpdates: 5, // web-specific: mobile default is 1.5 s (shared constant)
  }

  private request?: Promise<any>
  private permissionStatus?: PermissionStatus
  private watchId: number | null = null
  private throttledWatchHandler: ReturnType<typeof _.throttle> | null = null

  constructor(props: IUI2GeolocationFieldProps) {
    super(props)
    this.state = {
      isLocating: false,
      internalValue: props.value,
    }
  }

  public static getDerivedStateFromProps(
    nextProps: IUI2GeolocationFieldProps,
    prevState: IUI2GeolocationFieldState,
  ): Partial<IUI2GeolocationFieldState> | null {
    if (nextProps.value !== prevState.internalValue) {
      return { internalValue: nextProps.value }
    }
    return null
  }

  public componentDidMount() {
    const { isDisabled, isReadOnly } = this.props
    if (isDisabled || isReadOnly) return

    this.setState({ isLocating: true })
    if (CACHED_LOCATION) this.fireChange(CACHED_LOCATION)
    this.requestLocation()
    this.startWatching()
    this.requestLocationPermission()
  }

  public componentWillUnmount() {
    this.request?.cancel()
    if (this.permissionStatus) this.permissionStatus.onchange = null
    if (this.watchId !== null) {
      navigator.geolocation?.clearWatch(this.watchId)
      this.watchId = null
    }
    this.throttledWatchHandler?.cancel()
  }

  public render() {
    const { className } = this.props

    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-geolocationField', className)}
        renderReadOnly={this.renderReadOnly}
      >
        {this.renderEditable()}
      </UI2FieldShell>
    )
  }

  private renderReadOnly = (value: any, { editNode }: { editNode?: React.ReactNode } = {}): React.ReactNode => {
    const { showGPSAsDegree } = this.props
    let content: React.ReactNode
    if (!value || (!value.latitude && !value.longitude)) {
      content = <UI2NoValueAvailable />
    } else {
      const text = Formatter.formatGeolocation(value, showGPSAsDegree)
      if (!text) {
        content = <UI2NoValueAvailable />
      } else {
        content = (
          <div className='ui2-geolocationField-readOnly'>
            <span>{text}</span>
          </div>
        )
      }
    }
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private renderEditable(): React.ReactNode {
    const { isDisabled, showGPSAsDegree, watchPosition, allowRelocate } = this.props
    const { gpsError, isLocating, internalValue } = this.state

    const warningText = getLocationWarning(gpsError)
    const displayText = internalValue
      ? Formatter.formatGeolocation(internalValue, showGPSAsDegree)
      : undefined
    const mapUrl = internalValue ? Formatter.formatGeolocationAsGoogleMapUrl(internalValue) : undefined
    const showRelocate = allowRelocate && !watchPosition && !isDisabled

    return (
      <div
        className={classNames('ui2-geolocationField-body', {
          'ui2-geolocationField-body--disabled': isDisabled,
        })}
      >
        {isLocating && !displayText && !warningText && (
          <span className='ui2-geolocationField-status'>Locating…</span>
        )}
        {displayText && (
          mapUrl && !isDisabled ? (
            <a
              className='ui2-geolocationField-valueRow'
              href={mapUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Icon icon={IconNames.Geolocation} iconSize={14} className='ui2-geolocationField-icon' />
              <span className='ui2-geolocationField-value'>{displayText}</span>
            </a>
          ) : (
            <span className='ui2-geolocationField-valueRow'>
              <Icon icon={IconNames.Geolocation} iconSize={14} className='ui2-geolocationField-icon' />
              <span className='ui2-geolocationField-value'>{displayText}</span>
            </span>
          )
        )}
        {warningText && (
          <span className='ui2-geolocationField-warning'>
            <Icon icon={IconNames.WarningSign} iconSize={12} />
            {warningText}
          </span>
        )}
        {showRelocate && (
          <button
            className='ui2-geolocationField-retryButton'
            type='button'
            onClick={this.handleRelocate}
            aria-label='Re-locate'
          >
            Re-locate
          </button>
        )}
      </div>
    )
  }

  private fireChange = (coords: { latitude: number; longitude: number }) => {
    const { onChange } = this.props
    const { internalValue } = this.state
    if (!_.isEqual(internalValue, coords)) {
      onChange?.(coords)
    }
  }

  private requestLocation = () => {
    this.setState({ gpsError: undefined, isLocating: true })
    this.request = getCurrentGeoPosition().then(
      (position: GeolocationPosition) => {
        const { longitude, latitude } = position.coords
        CACHED_LOCATION = { longitude, latitude }
        this.fireChange(CACHED_LOCATION)
        this.setState({ isLocating: false })
      },
      (gpsError: GeolocationError) => {
        this.setState({ gpsError, isLocating: false })
      },
    )
  }

  private requestLocationPermission() {
    void navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permissionStatus: PermissionStatus) => {
        this.permissionStatus = permissionStatus
        permissionStatus.onchange = () => {
          if (permissionStatus.state !== 'granted') return
          this.setState({ gpsError: undefined })
          this.requestLocation()
          this.startWatching()
        }
      })
  }

  private startWatching = () => {
    if (!navigator.geolocation) return
    const { minSecondsBetweenUpdates = 5, watchPosition } = this.props
    if (!watchPosition) return

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId)
      this.throttledWatchHandler?.cancel()
      this.watchId = null
    }

    this.throttledWatchHandler = _.throttle((position: GeolocationPosition) => {
      const { longitude, latitude } = position.coords
      if (CACHED_LOCATION?.latitude === latitude && CACHED_LOCATION?.longitude === longitude) return
      CACHED_LOCATION = { longitude, latitude }
      this.fireChange(CACHED_LOCATION)
      this.setState({ isLocating: false })
    }, minSecondsBetweenUpdates * 1000)

    this.watchId = navigator.geolocation.watchPosition(
      this.throttledWatchHandler,
      (nativeError: GeolocationPositionError) => {
        this.setState({ gpsError: { nativeError }, isLocating: false })
      },
      { enableHighAccuracy: true, timeout: 10000 },
    )
  }

  private handleRelocate = () => {
    this.requestLocation()
  }
}

function getLocationWarning(gpsError: GeolocationError | undefined): string | undefined {
  if (!gpsError) return undefined
  if (gpsError.nativeError?.code === GeolocationPositionError.PERMISSION_DENIED) {
    return 'Enable location access in your browser settings'
  } else if (gpsError.nativeError?.code === GeolocationPositionError.POSITION_UNAVAILABLE) {
    return 'Location unavailable'
  } else if (gpsError.nativeError?.code === GeolocationPositionError.TIMEOUT) {
    return 'Location timed out'
  }
  return gpsError.message
}
